import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Star } from 'lucide-react';
import { UserRole } from '@kick/types';
import { api, getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useAuth } from '@/lib/auth-store';
import { Boton, Cargando, ErrorCarga, Vacio } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

interface Calificacion {
  id: string;
  orderId: string;
  code: string;
  merchantScore: number;
  courierScore: number | null;
  comment: string | null;
  merchantReply: string | null;
  createdAt: string;
  customerName: string;
  /** Solo en la vista de administración. */
  merchantName?: string;
}

/**
 * Calificaciones.
 *
 * El comercio ve las suyas y puede responder una vez. El administrador ve las
 * peores de toda la plataforma, que es lo único que necesita para actuar: las
 * buenas no requieren que nadie haga nada.
 */
export function Calificaciones() {
  /**
   * El comercio se lee del almacén, no con `useMerchantId()`.
   *
   * Ese hook lanza si la cuenta no tiene comercio, y esta pantalla la abre
   * también el administrador —que por definición no tiene ninguno—. Con el
   * hook, entrar aquí como admin reventaba la pantalla entera.
   */
  const { user, merchantId } = useAuth();
  const admin = user?.role === UserRole.ADMIN;
  const cliente = useQueryClient();
  const avisos = useAvisos();
  const [soloSinResponder, setSoloSinResponder] = useState(false);

  const lista = useQuery({
    queryKey: ['calificaciones', admin ? 'peores' : merchantId, soloSinResponder],
    queryFn: async () =>
      (
        await api.get<Calificacion[]>(
          admin
            ? '/admin/calificaciones/peores?limit=50'
            : `/merchants/${merchantId!}/calificaciones?limit=50${soloSinResponder ? '&sinResponder=true' : ''}`,
        )
      ).data,
    enabled: admin || !!merchantId,
  });

  const responder = useMutation({
    mutationFn: async ({ id, reply }: { id: string; reply: string }) =>
      (await api.post(`/calificaciones/${id}/responder`, { reply })).data,
    onSuccess: () => {
      avisos.exito('Respuesta publicada');
      void cliente.invalidateQueries({ queryKey: ['calificaciones'] });
    },
    onError: (e) => avisos.error(getErrorMessage(e)),
  });

  if (lista.isLoading) return <Cargando />;
  if (lista.isError) {
    return (
      <ErrorCarga mensaje={getErrorMessage(lista.error)} onReintentar={() => void lista.refetch()} />
    );
  }

  const filas = lista.data ?? [];

  return (
    <div className="mx-auto max-w-4xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl text-kick-dark">
            {admin ? 'Calificaciones bajas' : 'Lo que opinan tus clientes'}
          </h1>
          <p className="mt-1 text-sm text-kick-gray">
            {admin
              ? 'Menos de cuatro estrellas en toda la plataforma, de la peor a la mejor.'
              : 'Responder una mala calificación cuenta más que diez buenas sin responder.'}
          </p>
        </div>

        {!admin && (
          <button
            onClick={() => setSoloSinResponder((v) => !v)}
            aria-pressed={soloSinResponder}
            className={cn(
              'rounded-kick border px-3 py-1.5 text-sm font-semibold transition',
              soloSinResponder
                ? 'border-kick-red bg-kick-red text-white'
                : 'border-kick-gray/25 text-kick-ink hover:border-kick-gray/50',
            )}
          >
            Sin responder
          </button>
        )}
      </div>

      {filas.length === 0 ? (
        <div className="mt-6">
          <Vacio
            icono={<Star className="h-6 w-6" />}
            titulo={admin ? 'Ninguna calificación baja' : 'Todavía no te han calificado'}
            descripcion={
              admin
                ? 'Nadie ha puntuado por debajo de cuatro estrellas.'
                : 'Aparecerán aquí en cuanto un cliente califique un pedido entregado.'
            }
          />
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {filas.map((c) => (
            <Tarjeta
              key={c.id}
              calificacion={c}
              admin={admin}
              guardando={responder.isPending}
              onResponder={(reply) => responder.mutate({ id: c.id, reply })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Tarjeta({
  calificacion: c,
  admin,
  guardando,
  onResponder,
}: {
  calificacion: Calificacion;
  admin: boolean;
  guardando: boolean;
  onResponder: (reply: string) => void;
}) {
  const [respondiendo, setRespondiendo] = useState(false);
  const [texto, setTexto] = useState('');

  return (
    <section className="card-kick p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <Estrellas valor={c.merchantScore} />
          <p className="mt-1 text-sm text-kick-ink">
            {c.customerName}
            <span className="text-kick-gray"> · {c.code}</span>
          </p>
          {admin && c.merchantName && (
            <p className="text-sm font-semibold text-kick-dark">{c.merchantName}</p>
          )}
        </div>
        <p className="text-xs text-kick-gray">
          {new Date(c.createdAt).toLocaleDateString('es-CO', {
            day: 'numeric',
            month: 'short',
            timeZone: 'America/Bogota',
          })}
        </p>
      </div>

      {c.comment && (
        <p className="mt-3 rounded-kick bg-kick-light px-3 py-2 text-sm text-kick-ink">
          {c.comment}
        </p>
      )}

      {c.courierScore != null && (
        <p className="mt-2 text-xs text-kick-gray">
          Al domiciliario: {c.courierScore} de 5
        </p>
      )}

      {c.merchantReply ? (
        <div className="mt-3 rounded-kick border-l-4 border-kick-red bg-kick-light px-3 py-2">
          <p className="text-xs font-bold uppercase tracking-wide text-kick-gray">Tu respuesta</p>
          <p className="mt-0.5 text-sm text-kick-ink">{c.merchantReply}</p>
        </div>
      ) : (
        !admin && (
          <div className="mt-3">
            {respondiendo ? (
              <div className="space-y-2">
                <textarea
                  rows={3}
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  maxLength={500}
                  placeholder="Gracias por avisarnos. Lo revisamos con el equipo de cocina…"
                  aria-label="Respuesta a la calificación"
                  className="w-full rounded-kick border border-kick-gray/30 px-3 py-2 text-sm text-kick-ink outline-none transition focus:border-kick-red"
                />
                <div className="flex gap-2">
                  <Boton
                    onClick={() => onResponder(texto.trim())}
                    cargando={guardando}
                    disabled={texto.trim().length < 5}
                  >
                    Publicar respuesta
                  </Boton>
                  <Boton variante="secundario" onClick={() => setRespondiendo(false)}>
                    Cancelar
                  </Boton>
                </div>
                <p className="text-xs text-kick-gray">
                  Se publica junto a la opinión y solo se puede responder una vez.
                </p>
              </div>
            ) : (
              <Boton
                variante="secundario"
                className="px-3 py-1.5 text-xs"
                icono={<MessageSquare className="h-3.5 w-3.5" />}
                onClick={() => setRespondiendo(true)}
              >
                Responder
              </Boton>
            )}
          </div>
        )
      )}
    </section>
  );
}

function Estrellas({ valor }: { valor: number }) {
  return (
    <div className="flex gap-0.5" aria-label={`${valor} de 5 estrellas`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          aria-hidden
          className={cn(
            'h-4 w-4',
            n <= valor ? 'fill-amber-400 text-amber-400' : 'text-kick-gray/30',
          )}
        />
      ))}
    </div>
  );
}
